'use client'; // Need this for Swiper hooks/components

import React from 'react';
import Link from 'next/link';
import useEmblaCarousel from 'embla-carousel-react'; // Import Embla hook
import Script from 'next/script'; // Import Next.js Script component

// videoSlides now updated to use Vimeo for all, with placeholder titles for uniqueness if needed
// Each object will have youtubeId (null if Vimeo) and vimeoUrl (null if YouTube)
const videoSlides = [
  {
    title: 'Pharma Trends 2025 (Clip 1)',
    isVimeo: true,
    vimeoUrl: 'https://player.vimeo.com/video/1087896672?badge=0&autopause=0&player_id=0&app_id=58479',
    youtubeId: null,
  },
  {
    title: 'DJ-SALES (Clip 2)',
    isVimeo: true,
    vimeoUrl: 'https://player.vimeo.com/video/1087889173?badge=0&autopause=0&player_id=0&app_id=58479',
    youtubeId: null,
  },
  {
    title: 'DJ-CHANNEL (Clip 3)',
    isVimeo: true,
    vimeoUrl: 'https://player.vimeo.com/video/1087890160?badge=0&autopause=0&player_id=0&app_id=58479',
    youtubeId: null,
  },
  {
    title: 'DJ-INFLUENCE (Clip 4)',
    isVimeo: true,
    vimeoUrl: 'https://player.vimeo.com/video/1087890752?badge=0&autopause=0&player_id=0&app_id=58479',
    youtubeId: null,
  },
  {
    title: 'Rare Diseases (Clip 5)',
    isVimeo: true,
    vimeoUrl: 'https://player.vimeo.com/video/1087895870?badge=0&autopause=0&player_id=0&app_id=58479',
    youtubeId: null,
  },
  {
    title: 'How to Tank your Product Launch (Clip 6)',
    isVimeo: true,
    vimeoUrl: 'https://player.vimeo.com/video/1087896314?badge=0&autopause=0&player_id=0&app_id=58479',
    youtubeId: null,
  },
];

export default function Home() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    slidesToScroll: 1,
  });

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <>
      <Script src="https://player.vimeo.com/api/player.js" strategy="lazyOnload" />
      {/* Fold Section: Hero + Video Carousel in one screen */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white min-h-screen flex flex-col justify-between">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 flex-1">
          <div className="max-w-4xl mx-auto text-center h-full flex flex-col items-center justify-center gap-6">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight mt-10 md:mt-16">
              AI-Powered HCP Insights{' '}
              <span className="block text-[#59b9c1]">At Doctor Level</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-5 leading-relaxed">
              Unlock doctor-level insights and personalise HCP engagement using Data Jukebox&apos;s AI platform. Understand sales potential, influence, and channel preference.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/benchmark"
                className="relative bg-[#59b9c1] text-white px-7 py-3 rounded-lg text-lg font-semibold hover:bg-[#4a9ba2] transition-colors duration-300 inline-flex items-center gap-2"
              >
                Benchmark
                <span className="ml-1 text-xs font-bold bg-white/20 rounded-full px-2 py-0.5">NEW</span>
              </Link>
              <Link
                href="/products"
                className="bg-[#59b9c1] text-white px-7 py-3 rounded-lg text-lg font-semibold hover:bg-[#4a9ba2] transition-colors duration-300 inline-block"
              >
                Explore Products
              </Link>
              <Link
                href="/contact"
                className="border-2 border-[#59b9c1] text-[#59b9c1] px-7 py-3 rounded-lg text-lg font-semibold hover:bg-[#59b9c1] hover:text-white transition-colors duration-300 inline-block"
              >
                Get Started
              </Link>
          </div>
          </div>
        </div>
        
        {/* Carousel with modest minimum height; spacing distributed by justify-between */}
        <div className="bg-gray-100/5 pt-0 pb-4 min-h-[300px] md:min-h-[360px]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full">
            <div className="max-w-6xl mx-auto h-full">
              <div className="embla h-full overflow-hidden" ref={emblaRef}>
                <div className="embla__container flex h-full">
                  {videoSlides.map((slide, index) => (
                    <div key={index} className="embla__slide flex-[0_0_100%] md:flex-[0_0_50%] min-w-0">
                      <div className="bg-white rounded-lg shadow-lg overflow-hidden mx-3">
                        <div className="aspect-video relative">
                          <iframe
                            src={slide.vimeoUrl}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={slide.title}
                          ></iframe>
                        </div>
                        <div className="p-3">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900">
                            {slide.title}
                          </h3>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center mt-3 space-x-3">
                <button
                  onClick={scrollPrev}
                  className="p-3 bg-[#59b9c1] text-white rounded-full hover:bg-[#4a9ba2] transition-colors"
                  aria-label="Previous video"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={scrollNext}
                  className="p-3 bg-[#59b9c1] text-white rounded-full hover:bg-[#4a9ba2] transition-colors"
                  aria-label="Next video"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Data Jukebox?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our AI-powered platform delivers actionable insights for pharmaceutical companies
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#59b9c1] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI-Powered Insights</h3>
              <p className="text-gray-600">
                Advanced analytics and machine learning to understand HCP behavior and preferences
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#59b9c1] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Personalized Engagement</h3>
              <p className="text-gray-600">
                Tailored communication strategies based on individual HCP preferences and behaviors
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#59b9c1] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Performance Optimization</h3>
              <p className="text-gray-600">
                Continuous improvement of engagement strategies through data-driven insights
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your HCP Engagement?
          </h2>
          <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
            Join leading pharmaceutical companies using Data Jukebox to drive better outcomes
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/benchmark"
              className="relative bg-[#59b9c1] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#4a9ba2] transition-colors duration-300 inline-flex items-center gap-2"
            >
              Take Benchmark
              <span className="ml-1 text-xs font-bold bg-white/20 rounded-full px-2 py-0.5">NEW</span>
            </Link>
            <Link
              href="/contact"
              className="bg-[#59b9c1] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#4a9ba2] transition-colors duration-300 inline-block"
            >
              Get Started Today
            </Link>
            <Link
              href="/case-studies"
              className="border-2 border-[#59b9c1] text-[#59b9c1] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#59b9c1] hover:text-white transition-colors duration-300 inline-block"
            >
              View Case Studies
            </Link>
          </div>
          </div>
        </section>
    </>
  );
}
